import { DatasetWithData, PrimaryDataSet, DataValue, createColumnId, ColumnId, ColumnName } from '../../types';
import logger from './logger';
import { createDataProcessingError, createValidationError, AppError, ErrorCategory, ErrorSeverity } from '../../types/error.types';

/**
 * データセットへの安全なアクセスを提供するクラス
 * 列名と列IDの不整合問題を解決し、型安全なデータアクセスを実現する
 *
 * 注意: DatasetWithData型を要求することで、dataの存在を呼び出し側が保証する設計
 * DerivedDataSetでdataがない場合は、呼び出し側で事前に再評価（materialize）する責務を持つ
 */
export class DatasetAccessor {
  private nameToIdMap: Map<string, string>;
  private idToNameMap: Map<string, string>;

  constructor(private dataset: DatasetWithData) {
    this.nameToIdMap = new Map();
    this.idToNameMap = new Map();

    // スキーマから列名→列IDマッピングを作成
    const schema = dataset.schema;
    schema?.columns?.forEach(col => {
      this.nameToIdMap.set(col.name, col.id);
      this.idToNameMap.set(col.id, col.name);
    });
  }
  

  /**
   * 列名が存在するかチェック
   */
  hasColumn(columnName: ColumnName): boolean {
    return this.nameToIdMap.has(columnName);
  }

  /**
   * 列名から列IDを取得
   */
  getColumnId(columnName: ColumnName): ColumnId | undefined {
    const id = this.nameToIdMap.get(columnName);
    return id ? (id as ColumnId) : undefined;
  }

  /**
   * 列IDから列名を取得
   */
  getColumnName(columnId: ColumnId): ColumnName | undefined {
    const name = this.idToNameMap.get(columnId);
    return name ? (name as ColumnName) : undefined;
  }

  /**
   * データセットのスキーマを取得
   */
  getSchema() {
    return this.dataset.schema;
  }

  /**
   * 列名でデータにアクセス（内部で列IDに変換）
   */
  getColumnData(columnName: ColumnName): DataValue[] {
    const columnId = this.nameToIdMap.get(columnName);
    if (!columnId) {
      throw createValidationError(
        `Column "${columnName}" not found in dataset schema`,
        'columnName',
        columnName,
        'Must be one of the existing column names'
      );
    }

    // getColumnDataById()に委譲（型による分岐はそちらで処理）
    return this.getColumnDataById(columnId as ColumnId);
  }

  /**
   * 列IDでデータにアクセス
   */
  async getColumnDataByIdAsync(columnId: string): Promise<DataValue[]> {
    // 非同期版も同期版と同じ動作に
    return this.getColumnDataById(columnId as ColumnId);
  }
  
  /**
   * データセット型に応じた適切なデータキーを取得
   * PrimaryDataSetのみcreateColumnId()を使用し、他は直接columnIdを使用
   */
  private getDataKey(columnId: string): ColumnId {
    return this.dataset.type === 'primary'
      ? createColumnId(columnId)
      : columnId as ColumnId;
  }

  /**
   * 列IDでデータにアクセス（同期版）
   */
  getColumnDataById(columnId: ColumnId): DataValue[] {
    // DatasetWithData型により、dataの存在は保証されている
    const dataKey = this.getDataKey(columnId);
    const columnData = this.dataset.data[dataKey];
    if (!columnData) {
      const columnName = this.idToNameMap.get(columnId) || 'Unknown';
      logger.error(`DatasetAccessor error: Column ID "${columnId}" not found. Available IDs:`, Object.keys(this.dataset.data));
      throw createDataProcessingError(
        `Column data for ID "${columnId}" (name: ${columnName}) not found in dataset`,
        'get_column_data_by_id',
        this.dataset.id,
        new Error(`Missing data for column ID: ${columnId}`)
      );
    }

    return columnData;
  }

  /**
   * 指定行のデータを列名ベースのオブジェクトで取得
   */
  getRowData(rowIndex: number): Record<string, DataValue> {
    const row: Record<string, DataValue> = {};

    this.dataset.schema?.columns?.forEach(col => {
      // getColumnDataById()に委譲（型による分岐はそちらで処理）
      const columnData = this.getColumnDataById(col.id);
      const value = columnData?.[rowIndex];
      row[col.name] = (value !== undefined) ? value : null;
    });

    return row;
  }

  /**
   * データセットの行数を取得
   */
  getRowCount(): number {
    return this.dataset.rowCount || 0;
  }

  /**
   * 利用可能な列名のリストを取得
   */
  getAvailableColumnNames(): ColumnName[] {
    return (this.dataset.schema?.columns?.map(col => col.name as ColumnName) || []);
  }

  /**
   * 列の型情報を取得
   */
  getColumnType(columnName: ColumnName): string {
    const column = this.dataset.schema?.columns?.find(col => col.name === columnName);
    return column?.type || 'unknown';
  }

  /**
   * 特定の行・列の値を取得
   */
  getValue(rowIndex: number, columnName: ColumnName): DataValue {
    const columnId = this.nameToIdMap.get(columnName);
    if (!columnId) {
      throw createValidationError(
        `Column "${columnName}" not found in dataset schema`,
        'columnName',
        columnName,
        'Must be one of the existing column names'
      );
    }

    // getColumnDataById()に委譲（型による分岐はそちらで処理）
    const columnData = this.getColumnDataById(columnId as ColumnId);
    const value = columnData[rowIndex];
    return (value !== undefined) ? value : null;
  }

  /**
   * 利用可能な列名のリストを取得（関数レジストリ用）
   */
  getColumnNames(): ColumnName[] {
    return this.getAvailableColumnNames();
  }

  /**
   * データセット整合性の検証
   */
  validateConsistency(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (this.dataset.type !== 'primary') {
      return { isValid: true, errors: [] }; // DerivedDataSetは今回はスキップ
    }

    const primaryDataset = this.dataset as PrimaryDataSet;

    // スキーマとデータキーの一致確認
    const schemaColumnIds = new Set(this.dataset.schema?.columns?.map(col => col.id) || []);
    const dataKeys = new Set(Object.keys(primaryDataset.data));
    
    const missingInData = [...schemaColumnIds].filter(id => !dataKeys.has(id));
    const extraInData = [...dataKeys].filter(key => !schemaColumnIds.has(createColumnId(key)));
    
    if (missingInData.length > 0) {
      errors.push(`Missing data for column IDs: ${missingInData.join(', ')}`);
    }
    
    if (extraInData.length > 0) {
      errors.push(`Extra data keys not in schema: ${extraInData.join(', ')}`);
    }

    // 各列のデータ長が行数と一致するかチェック
    const expectedRowCount = this.dataset.rowCount || 0;
    
    this.dataset.schema?.columns?.forEach(col => {
      const dataKey = this.getDataKey(col.id);
      const columnData = primaryDataset.data[dataKey];
      if (columnData && columnData.length !== expectedRowCount) {
        errors.push(`Column "${col.name}" (ID: ${col.id}) has ${columnData.length} rows, expected ${expectedRowCount}`);
      }
    });

    // 重大な整合性エラーの場合は例外を投げる
    if (errors.length > 0) {
      const criticalErrors = errors.filter(error => 
        error.includes('Missing data for column IDs') || 
        error.includes('has 0 rows') ||
        error.includes('undefined') ||
        error.includes('null')
      );
      
      if (criticalErrors.length > 0) {
        throw new AppError(
          `Critical dataset corruption detected: ${criticalErrors.join('; ')}`,
          ErrorCategory.DATA_PROCESSING,
          ErrorSeverity.CRITICAL,
          {
            recoverable: false,
            userMessage: 'Dataset validation failed. The data may be corrupted.',
            details: { 
              datasetId: this.dataset.id, 
              errors: criticalErrors,
              allWarnings: errors
            }
          }
        );
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}
